import {useCallback, useMemo} from 'react';

import type {OptionalGetter} from '../../mobx-toolbox';
import {expr, use} from '../../mobx-toolbox';
import type {DailyAchievement} from '../../TwelveXTwelvePress/computeDailyAchievement';
import clamp from '../../util/clamp';
import type {DailyAchievementProps} from './DailyAchievementBinding';
import type {ProgressTabKey} from './DailyAchievementTabViewProps';

export default function useDailyAchievementSlides(
  da: DailyAchievement,
  getTabKey?: OptionalGetter<ProgressTabKey>,
  setTabKey?: (_: ProgressTabKey) => void,
): readonly [
  slides: readonly {key: ProgressTabKey; props: DailyAchievementProps}[],
  getIndex: () => number,
  setIndex: (_: number) => void,
] {
  const slides = useMemo(() => {
    const result: {key: ProgressTabKey; props: DailyAchievementProps}[] = [
      {
        key: 'accumulative',
        props: {
          years: da.fullYearsReached,
          months: da.fullMonthsAfterYearsReached,
          days: da.fullDaysAfterMonthsReached,
        },
      },
    ];
    if (da.fullMonthsReached !== da.fullMonthsAfterYearsReached) {
      result.push({
        key: 'months',
        props: {months: da.fullMonthsReached},
      });
    }
    if (da.fullDaysReached !== da.fullDaysAfterMonthsReached) {
      result.push({
        key: 'days',
        props: {days: da.fullDaysReached},
      });
    }
    return result;
  }, [da]);

  const getIndex = useCallback(
    () =>
      expr(() => {
        const tabKey = use(getTabKey);
        if (tabKey === undefined) {
          return 0;
        }
        const index = slides.findIndex(_ => _.key === tabKey);
        return index === -1 ? 0 : index;
      }),
    [getTabKey, slides],
  );

  const setIndex = useCallback(
    (_: number) => {
      const normalized = Math.round(clamp(_, slides.length - 1));
      setTabKey?.(slides[normalized].key);
    },
    [setTabKey, slides],
  );

  return [slides, getIndex, setIndex];
}
